import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, ElementRef, ViewChild, inject } from '@angular/core';
import { Component, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogContent, MatDialogActions, MatDialogTitle } from '@angular/material/dialog';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { catchError, finalize, firstValueFrom, of } from 'rxjs';
import { TopLoaderService } from 'src/app/core/services/top-loader.service';
import { ConnectorPictureService } from 'src/app/shared/services/connector-picture.service';
import { FeedbackInfoComponent, SnackbarService } from 'ok-angular-shared/components';
import { ErrorMessage } from 'src/app/shared/models/errorMessage.model';
import moment from 'moment';
import { MatIcon } from '@angular/material/icon';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { DatePipe } from '@angular/common';
import { MatInputModule, MatInput } from '@angular/material/input';
import { MatButton, MatIconButton } from '@angular/material/button';
import { LocationDetailsConnector } from '../charging-station-location-details/charging-station-locations-details.model';

@Component({
  selector: 'adm-connector-image-dialog',
  templateUrl: './connector-image-dialog.component.html',
  styleUrls: ['./connector-image-dialog.component.scss'],
  imports: [
    FeedbackInfoComponent,
    MatIcon,
    MatDialogContent,
    MatDialogActions,
    MatDialogTitle,
    MatFormField,
    MatLabel,
    MatDatepicker,
    MatDatepickerInput,
    MatInput,
    MatInputModule,
    MatButton,
    MatIconButton,
    DatePipe,
  ],
})
export class ConnectorImageDialogComponent implements OnInit {
  dialogRef = inject<MatDialogRef<ConnectorImageDialogComponent>>(MatDialogRef);
  data = inject<{
    text: string;
    header: string;
    src: string;
    connector: LocationDetailsConnector;
    editMode: boolean;
  }>(MAT_DIALOG_DATA);
  private connectorPictureService = inject(ConnectorPictureService);
  private snackbarService = inject(SnackbarService);
  topLoaderService = inject(TopLoaderService);
  private sanitizer = inject(DomSanitizer);
  private changeDetectorRef = inject(ChangeDetectorRef);

  public text: string;
  public header: string;
  public src: string;
  public connector: LocationDetailsConnector;
  public editMode: boolean;
  public fileToUpload: File | null = null;
  public pictureTakenDate: Date | null = null;
  public pictureOutdated: boolean;
  public updated: boolean;
  public previewImageUrlRaw?: string;
  public previewImageSafeUrl?: SafeUrl;
  @ViewChild('fileUpload') fileInputElement: ElementRef<HTMLInputElement>;
  public allowedFileExtensions = ['jpg', 'jpeg'];
  public hideUpload = false;
  public hideImage = false;
  public errorMessage: string | null = null;

  async ngOnInit() {
    this.text = this.data?.text;
    this.header = this.data?.header;
    this.src = `${this.data?.src}?${new Date().getTime()}`;
    this.connector = this.data?.connector;
    this.editMode = this.data?.editMode;
    await this.fetchPictureTakenDateAndCalculateOutdated();
  }

  todayDate: Date = new Date();
  showUnsavedPreviewWarning: boolean;

  onClickCloseDialog() {
    // If no file to upload, close immediately
    if (!this.fileToUpload) {
      this.dialogRef.close(true);
      return;
    }

    // If warning already shown, close dialog
    if (this.showUnsavedPreviewWarning) {
      this.dialogRef.close(true);
      return;
    }

    // Show warning on first attempt to close with unsaved file
    this.showUnsavedPreviewWarning = true;
  }

  clearPreview() {
    this.fileToUpload = null;
    this.showUnsavedPreviewWarning = false;
  }

  onImageError() {
    this.hideImage = true;
    this.hideUpload = false;
  }

  handleFileInput(ev: Event) {
    // reset error message
    this.errorMessage = null;

    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];

    if (!file) return;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension) {
      this.fileInputElement.nativeElement.value = '';
      this.errorMessage = `Den valgte fil er ugyldig.`;
      return;
    }
    if (!this.allowedFileExtensions.includes(fileExtension)) {
      this.fileInputElement.nativeElement.value = '';
      this.errorMessage = `Den valgte fil er ugyldig. Skal være en af følgende filtyper: ${this.allowedFileExtensions.join(', ')}`;
      return;
    }
    if (this.previewImageUrlRaw) {
      URL.revokeObjectURL(this.previewImageUrlRaw);
    }
    this.fileToUpload = file;
    this.previewImageUrlRaw = URL.createObjectURL(file);
    this.previewImageSafeUrl = this.sanitizer.bypassSecurityTrustUrl(this.previewImageUrlRaw);
  }

  changeDate(event) {
    this.pictureTakenDate = event.value;
  }

  private async fetchPictureTakenDateAndCalculateOutdated() {
    try {
      const resp = await firstValueFrom(this.connectorPictureService.getConnectorPictureTakenDay(this.connector.identifier));
      if (resp) {
        const parsedDate = new Date(resp);
        if (!isNaN(parsedDate.getTime())) {
          this.pictureTakenDate = parsedDate;
        }
      }
    } catch {
      // connector does not have a picture taken date
    }

    const sixMonthsMilis = 1000 * 60 * 60 * 24 * 31 * 6;
    this.pictureOutdated = !!this.pictureTakenDate && this.pictureTakenDate.getTime() < Date.now() - sixMonthsMilis;
    this.changeDetectorRef.detectChanges();
  }

  uploadSelectedFile() {
    if (!this.fileToUpload) {
      return;
    }

    this.topLoaderService.setLoading(true);
    this.connectorPictureService
      .uploadConnectorPicture(this.connector.identifier, this.fileToUpload, moment(this.pictureTakenDate))
      .pipe(
        catchError((httpError: HttpErrorResponse) => {
          if (httpError.status == 400 && httpError.error) {
            const error = httpError.error as ErrorMessage;
            if (error.code === 350000) {
              this.errorMessage = `Filen kunne ikke uploades, da filtypen ikke er understøttet.`;
            }
            if (error.code === 350001) {
              this.errorMessage = `Filen kunne ikke uploades, da den er tom.`;
            }
          } else if (httpError.status == 413) {
            this.snackbarService.warning(`Filen kunne ikke uploades, da filstørrelsen er for stor.`);
          } else {
            this.errorMessage = `Filen kunne ikke uploades.`;
          }
          return of();
        }),
        finalize(() => {
          this.topLoaderService.setLoading(false);
        })
      )
      .subscribe(() => {
        this.errorMessage = '';
        this.snackbarService.success('Billede blev uploadet');
        this.src = `${this.data?.src}?${new Date().getTime()}`;
        this.updated = true;
        this.fileToUpload = null;
        this.fileInputElement.nativeElement.value = '';
        this.hideUpload = true;
        this.hideImage = false;
      });
  }
}
